// Next3DaysRail — sticky right-side widget showing every event in the
// next 72 hours (Today + Tomorrow + Day 3), grouped by day, color-coded
// by layer. Lives on the V3 Dashboard's right rail on large screens.
//
// Pulls from /api/v3/calendar/events using the calendar router already
// wired up in Phase 1.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDays, endOfDay, startOfDay, format, parseISO, isSameDay,
} from "date-fns";
import { CalendarDays, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const LAYER_STYLE = {
  task:      { color: "#0EA5E9", label: "Task"    },
  deal:      { color: "#10B981", label: "Deal"    },
  build:     { color: "#F59E0B", label: "Build"   },
  closing:   { color: "#059669", label: "Closing" },
  jarvis:    { color: "#A855F7", label: "Jarvis"  },
  personal:  { color: "#64748B", label: "Event"   },
  marketing: { color: "#EF4444", label: "Marketing" },
};

function eventTime(e) {
  if (e.all_day) return "All day";
  try { return format(parseISO(e.starts_at), "h:mm a"); }
  catch { return ""; }
}

export default function Next3DaysRail() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const res = await api.get("/v3/calendar/events", {
          params: {
            start:  startOfDay(now).toISOString(),
            end:    endOfDay(addDays(now, 2)).toISOString(),
            layers: "task,build,deal,closing,jarvis,personal,marketing",
          },
        });
        if (!cancelled) setEvents(res.data?.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dayGroups = useMemo(() => {
    const now  = new Date();
    const days = [now, addDays(now, 1), addDays(now, 2)];
    return days.map((d) => ({
      date:  d,
      label: isSameDay(d, now)
        ? "Today"
        : isSameDay(d, addDays(now, 1))
          ? "Tomorrow"
          : format(d, "EEEE"),
      sub:   format(d, "MMM d"),
      items: events.filter((e) => {
        try { return isSameDay(parseISO(e.starts_at), d); }
        catch { return false; }
      }),
    }));
  }, [events]);

  const totalCount = events.length;

  return (
    <aside
      data-testid="dashboard-next-3-days-rail"
      className="rounded-2xl bg-white border border-slate-200/70 shadow-sm overflow-hidden lg:sticky lg:top-24 self-start"
    >
      <div className="px-4 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg grid place-items-center text-white shadow-sm bg-gradient-to-br from-violet-400 to-purple-600">
            <CalendarDays className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-[9.5px] font-bold uppercase tracking-widest text-slate-500 leading-none">
              What's next
            </div>
            <div className="text-[13px] font-bold text-slate-900 leading-tight mt-0.5">
              Next 3 days
            </div>
          </div>
        </div>
        <span
          className="text-[10px] font-bold text-slate-500 tabular-nums bg-slate-100 rounded-full px-2 py-0.5"
          data-testid="dashboard-next-3-days-count"
        >
          {loading ? "…" : totalCount}
        </span>
      </div>

      <div className="p-3 max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center gap-2 text-[11.5px] text-slate-500 py-6 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : totalCount === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center">
            <div className="text-[12px] text-slate-700 font-semibold">
              Nothing on the horizon
            </div>
            <div className="mt-1 text-[11px] text-slate-500 leading-snug">
              Add events, tasks, or deal deadlines and they'll surface here.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {dayGroups.map((g) => (
              <div key={g.label} data-testid={`dashboard-rail-day-${g.label.toLowerCase()}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1.5 px-1">
                  <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">
                    {g.label}
                  </div>
                  <div className="text-[10px] text-slate-400 tabular-nums">
                    {g.sub}
                  </div>
                </div>
                {g.items.length === 0 ? (
                  <div className="rounded-md bg-slate-50/60 border border-dashed border-slate-200 text-[10.5px] text-slate-400 px-2.5 py-1.5 italic">
                    Nothing scheduled
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {g.items.slice(0, 5).map((e) => {
                      const style = LAYER_STYLE[e.layer] || LAYER_STYLE.personal;
                      return (
                        <li key={e.id}>
                          <Link
                            to={e.property_id ? `/v3/properties/${e.property_id}` : "/v3/calendar"}
                            data-testid={`dashboard-rail-event-${e.id}`}
                            className="group flex items-start gap-2 rounded-md hover:bg-slate-50 px-2 py-1.5 transition-colors"
                          >
                            <span
                              aria-hidden="true"
                              className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: e.color || style.color }}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-[12px] font-semibold text-slate-900 leading-tight truncate group-hover:text-amber-700">
                                {e.title}
                              </div>
                              <div className="mt-0.5 text-[10px] text-slate-500 leading-tight truncate">
                                {eventTime(e)}
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                    {g.items.length > 5 && (
                      <li>
                        <Link
                          to="/v3/calendar"
                          className="block text-center text-[10.5px] font-semibold text-amber-600 hover:text-amber-700 px-2 py-1"
                        >
                          +{g.items.length - 5} more
                        </Link>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 px-4 py-2.5">
        <Link
          to="/v3/calendar"
          data-testid="dashboard-next-3-days-open-calendar"
          className="w-full inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold text-amber-600 hover:text-amber-700"
        >
          Open full Calendar <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </aside>
  );
}
